import { create } from 'zustand';
import { AuthState, AuthError, AuthErrorType, AuthTokens, LoginCredentials, User } from '../types/auth';
import { UserRole } from '../types/roles';
import { storage } from '../utils/storage';
import { apiService } from '../services/apiService';
import { clearLegacyAuthStorage, hasSessionHint } from '../utils/authSession';

// Define the LoginResponse interface
interface LoginResponse {
  user: {
    id: number | string;
    email?: string;
    whatsappNumber?: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  access_token?: string;
  refresh_token?: string;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  tokens: null,
  loading: {
    login: false,
    logout: false,
    refresh: false
  },
  error: null
};

// Initialize state from storage
const getInitialState = (): AuthState => {
  try {
    const storedUser = storage.getUser();
    if (storedUser) {
      return {
        ...initialState,
        user: storedUser,
        tokens: null,
        isAuthenticated: true,
      };
    }

    // If no valid stored data, return initial state
    return initialState;
  } catch (error) {
    console.error('Error initializing auth state:', error);
    // Clear corrupted data
    storage.clearAuth();
    clearLegacyAuthStorage();
    return initialState;
  }
};

interface AuthStore extends AuthState {
  login: (credentials: LoginCredentials) => Promise<User | null>;
  logout: () => Promise<void>;
  setUser: (user: LoginResponse['user']) => void;
  setTokens: (tokens: AuthTokens | null) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: AuthError | null) => void;
  clearError: () => void;
  initializeAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...getInitialState(),
  
  initializeAuth: async () => {
    if (!hasSessionHint()) {
      get().clearAuth();
      return;
    }
    try {
      const response = await apiService.request<LoginResponse['user']>({
        url: '/auth/me',
        method: 'GET',
      });
      const user = response.data;
      if (user) {
        get().setUser(user);
        set({ isAuthenticated: true, error: null });
        return;
      }
    } catch (error) {
      get().clearAuth();
    }
  },
  
  login: async (credentials: LoginCredentials): Promise<User | null> => {
    set({ loading: { ...initialState.loading, login: true }, error: null });
    try {
      console.log('Sending login request with:', credentials);
      const response = await apiService.login(credentials);
      
      console.log('Server response:', response);
      
      if (!response.user) {
        throw new Error('Invalid response format from server');
      }

      apiService.resetRedirectFlag(); // Reset the redirect flag on successful login

      const transformedUser: User = {
        id: response.user.id.toString(),
        email: response.user.email || null,
        whatsappNumber: response.user.whatsappNumber || null,
        firstName: response.user.firstName!,
        lastName: response.user.lastName!,
        role: response.user.role as UserRole,
        isAuthenticated: true
      };

      storage.setUser(transformedUser);
      clearLegacyAuthStorage();
      await apiService.waitForSessionReady();

      // Store in Zustand state
      set({
        isAuthenticated: true,
        user: transformedUser,
        tokens: null,
        loading: { login: false, logout: false, refresh: false },
        error: null
      });

      return transformedUser;
    } catch (error: any) {
      get().clearAuth();
      console.error('Login error in store:', error);
      
      if (error.isAxiosError) {
        if (error.status === 401) {
          set({
            error: {
              type: AuthErrorType.INVALID_CREDENTIALS,
              message: error.message
            },
            loading: { ...initialState.loading, login: false }
          });
        } else {
          set({
            error: {
              type: AuthErrorType.NETWORK_ERROR,
              message: error.message || 'Network error occurred. Please try again.'
            },
            loading: { ...initialState.loading, login: false }
          });
        }
      } else {
        set({
          error: {
            type: AuthErrorType.UNKNOWN,
            message: error instanceof Error ? error.message : 'An unexpected error occurred'
          },
          loading: { ...initialState.loading, login: false }
        });
      }
      return null;
    }
  },
  
  logout: async () => {
    // Clear auth immediately (don't wait for API)
    set(initialState);
    
    // Clear all storage
    storage.clearAuth();
    localStorage.removeItem('user');
    clearLegacyAuthStorage();

    // Remove auth token from API service
    apiService.removeAuthToken();
    
    // Try to notify server (fire and forget - don't block on this)
    try {
      apiService.logout().catch(() => {
        // Ignore server logout errors
      });
    } catch {
      // Ignore any errors
    }
    
    // Navigate to login page immediately
    window.location.replace('/login');
  },
  
  setUser: (user: LoginResponse['user']) => {
    if (!user.firstName || !user.lastName) {
      throw new Error('First name and last name are required');
    }

    const transformedUser: User = {
      id: user.id.toString(),
      email: user.email || null,
      whatsappNumber: user.whatsappNumber || null,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role as UserRole,
      isAuthenticated: true
    };

    set({ user: transformedUser, isAuthenticated: true });
    storage.setUser(transformedUser);
  },
  
  setTokens: (tokens) => {
    set({ tokens });
    if (tokens) {
      clearLegacyAuthStorage();
    } else {
      storage.clearTokens();
    }
  },

  clearAuth: () => {
    storage.clearAuth();
    localStorage.removeItem('user');
    clearLegacyAuthStorage();
    apiService.removeAuthToken();
    apiService.resetRedirectFlag();
    set(initialState);
  },
  
  setLoading: (loading) => set(state => ({ 
    loading: { ...state.loading, login: loading } 
  })),
  
  setError: (error) => set({ error }),
  
  clearError: () => set({ error: null })
}));
